import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { t } from '../../../../services/i18nService';
import { getA11yAttributesV2 } from '../../../../services/a11yIIBScreenReaderService';

import confirmHardwareSettingsSelector from '../../../../selectors/testOverview/testComponents/hardwareAccessCheck/ConfirmHardwareSettingsSelector';
import liveSessioner from '../../../../services/livesession/LiveSessioner';
import livestreamActions from '../../../../actions/LivestreamActions';
import streamingStarterService from '../../../../services/livesession/StreamingStarterService';
import webcamHealthChecker from '../../../../services/livesession/webcamHealthChecker';
import micHealthChecker from '../../../../services/livesession/micHealthChecker';

import IconSelect from './IconSelect';
import LiveCamera from './LiveCamera';
import MicrophoneAudioLevelMeter from './MicrophoneAudioLevelMeter';
import PlayTestSoundButtonWithAudio from './PlayTestSoundButtonWithAudio';
import HardwareInstructions from './HardwareInstructions';
import WebcamDiagnosisFailed from './WebcamDiagnosisFailed';
import WebcamDeviceHealthCheckStatus from './WebcamDeviceHealthCheckStatus';
import MicDiagnosisFailed from './MicDiagnosisFailed';
import MicDeviceHealthCheckStatus from './MicDeviceHealthCheckStatus';
import apiService from '../../../../services/api/ApiService';

import styles from '../../testOverview.scss';

export class ConfirmHardwareSettings extends React.PureComponent {
    static propTypes = {
        audioDeviceId: PropTypes.string,
        audioDevices: PropTypes.array,
        dispatch: PropTypes.func.isRequired,
        hardwareCheckIsOffline: PropTypes.bool.isRequired,
        isHardwareSettingsSelectorButtonDisabled: PropTypes.bool,
        isConfirmHardwareSettingsButtonDisabled: PropTypes.bool,
        shouldShowMicrophoneAudioLevelMeter: PropTypes.bool,
        shouldShowHardwareInstructions: PropTypes.bool,
        showSelectAudioDevice: PropTypes.bool.isRequired,
        showSelectSpeakerDevice: PropTypes.bool.isRequired,
        showSelectWebcamDevice: PropTypes.bool.isRequired,
        showWarningMissingAudioDevice: PropTypes.bool.isRequired,
        showWarningMissingWebcamDevice: PropTypes.bool.isRequired,
        shouldShowWebcamDiagnosisFailed: PropTypes.bool,
        shouldShowWebcamHealthStatus: PropTypes.bool,
        shouldShowWebcamHealthCheckInProgress: PropTypes.bool,
        shouldShowMicDiagnosisFailed: PropTypes.bool,
        shouldShowMicHealthStatus: PropTypes.bool,
        shouldShowMicHealthCheckInProgress: PropTypes.bool,
        speakerDeviceId: PropTypes.string,
        speakerDevices: PropTypes.array,
        videoDevices: PropTypes.array,
        webcamDeviceId: PropTypes.string,
        webcamStream: PropTypes.object,
        shouldShowWebcamHealthInformation: PropTypes.bool.isRequired,
        shouldShowSpeakerInformation: PropTypes.bool.isRequired,
        shouldShowMicHealthInformation: PropTypes.bool,
        shouldMuteMicWhenSoundTestIsOn: PropTypes.bool,
        shouldCompleteHardwarePrechecks: PropTypes.bool,
    };

    componentWillUnmount() {
        streamingStarterService.cancelRecorderAlert();
    }

    handleConfirmSettings = async () => {
        const { dispatch, hardwareCheckIsOffline, webcamDeviceId, shouldCompleteHardwarePrechecks, audioDeviceId, audioDevices } = this.props;

        if (shouldCompleteHardwarePrechecks) {
            if (Array.isArray(audioDevices)) {
                const selectedMicrophone = audioDevices.find(device => device.deviceId === audioDeviceId);
                const deviceName = selectedMicrophone?.label || null;

                try {
                    await apiService.sendPrecheckCompleteSignal(deviceName);
                } catch (error) {
                    console.error(
                        `Failed to send hardware precheck completed signal for device: "${deviceName}".`,
                        'The following error occurred:',
                        error
                    );
                }
            } else {
                console.error(
                    'Failed to send precheck hardware completed signal: The `audioDevices` prop is not a valid array.',
                    'Received:', audioDevices
                );
            }
        }

        dispatch(livestreamActions.confirmHardwareSettings());

        if (!hardwareCheckIsOffline) {
            streamingStarterService.startStreaming(webcamDeviceId);
        }
    };

    handleSwitchWebcamDevice = (e) => {
        const { audioDeviceId, webcamDeviceId } = this.props;
        const options = e.currentTarget.options;
        const selectedOption = Array.from(options).find((option) => {
            return option.selected;
        });
        const selectedDeviceId = selectedOption.value;
        liveSessioner.switchDevices(selectedDeviceId, audioDeviceId, webcamDeviceId, audioDeviceId);
    };

    handleSwitchAudioDevice = (e) => {
        const { webcamDeviceId, audioDeviceId } = this.props;
        const options = e.currentTarget.options;
        const selectedOption = Array.from(options).find((option) => {
            return option.selected;
        });
        const selectedDeviceId = selectedOption.value;
        liveSessioner.switchDevices(webcamDeviceId, selectedDeviceId, webcamDeviceId, audioDeviceId);
    };

    handleSwitchSpeakerDevice = (e) => {
        const { dispatch } = this.props;
        const selectedDeviceId = e.target.value;

        dispatch(livestreamActions.switchSpeakerDevice(selectedDeviceId));
    };

    handleRetryWebcamHealthCheck = () => {
        const { webcamStream } = this.props;
        if (webcamStream) {
            webcamHealthChecker.resetAndStartWebcamHealthCheck(webcamStream);
        }
    };

    handleRetryMicHealthCheck = () => {
        const { webcamStream } = this.props;
        // Reset the mic health check status and restart monitoring
        micHealthChecker.resetAndRestart(webcamStream);
    };

    /**
     * Look at the comment in setSpeakerDeviceId in LivestreamReducer to learn
     * more.
     */
    speakerSelectValue(deviceList, deviceId) {
        if (deviceList.length < 1 || deviceId) {
            return deviceId;
        }

        return deviceList[0].deviceId;
    }

    improveDeviceLabel(deviceId, label) {
        /**
         * RP-2016: Add Default / Communications prefix when Chromium's Inspera
         * Exam Portal doesn't add it.
         */
        if (
            label.indexOf(' - ') === 0
            && (deviceId === 'default' || deviceId === 'communications')
        ) {
            return t(`hardwareAccessCheck.deviceLabel.${deviceId}`) + label;
        }

        return label;
    }

    /**
     * Constructs an accessible aria-label for device selection dropdowns.
     * The label includes the device type, health check status (if applicable), and selection state.
     *
     * @param {string} deviceType - The device type key (e.g., 'camera', 'microphone')
     * @param {boolean} diagnosisFailed - Whether the device health check failed
     * @param {boolean} healthCheckInProgress - Whether the device health check is currently running
     * @returns {string} The formatted aria-label string for screen readers
     *
     */
    getDeviceAriaLabel(deviceType, diagnosisFailed, healthCheckInProgress) {
        const baseLabel = t(`hardwareAccessCheck.${deviceType}Selection`);
        const selectedLabel = t('hardwareAccessCheck.selected');

        if (diagnosisFailed) {
            return `${baseLabel}, ${selectedLabel}`;
        }

        const healthCheckStatusLabel = healthCheckInProgress
            ? t('hardwareAccessCheck.checking')
            : t('hardwareAccessCheck.checked');

        return `${baseLabel} - ${healthCheckStatusLabel}, ${selectedLabel}`;
    }

    renderDeviceList(deviceList) {
        // React properly if null is given as deviceList.
        if (!deviceList) {
            return null;
        }

        return deviceList.map(({ deviceId, label }) => (
            <option key={deviceId} value={deviceId}>
                {this.improveDeviceLabel(deviceId, label)}
            </option>
        ));
    }

    render() {
        const {
            audioDeviceId,
            audioDevices,
            isHardwareSettingsSelectorButtonDisabled,
            shouldShowMicrophoneAudioLevelMeter,
            shouldShowHardwareInstructions,
            shouldShowMicHealthCheckInProgress,
            speakerDeviceId,
            speakerDevices,
            showSelectAudioDevice,
            showSelectSpeakerDevice,
            showSelectWebcamDevice,
            showWarningMissingWebcamDevice,
            showWarningMissingAudioDevice,
            shouldShowWebcamHealthStatus,
            shouldShowWebcamHealthCheckInProgress,
            shouldShowMicDiagnosisFailed,
            shouldShowMicHealthStatus,
            videoDevices,
            webcamDeviceId,
            webcamStream,
            isConfirmHardwareSettingsButtonDisabled,
            shouldShowWebcamHealthInformation,
            shouldShowSpeakerInformation,
            shouldShowWebcamDiagnosisFailed,
            shouldShowMicHealthInformation,
            shouldMuteMicWhenSoundTestIsOn,
        } = this.props;
        const isReadOnly = isHardwareSettingsSelectorButtonDisabled;

        const audioOptions = this.renderDeviceList(audioDevices);
        const speakerOptions = this.renderDeviceList(speakerDevices);
        const webcamOptions = this.renderDeviceList(videoDevices);
        const hardwareAccessCheckClassName = `${styles.hardwareSettings} 
                                                        ${shouldShowHardwareInstructions ? styles.hasHardwareInstructions : ''} 
                                                        ${shouldShowMicrophoneAudioLevelMeter ? styles.enhancedHardwarePreview : ''}`;


        return (
            <div className={hardwareAccessCheckClassName}>
                {shouldShowHardwareInstructions && <HardwareInstructions />}
                <div className={styles.hardwareContent}>
                    <div className={styles.hardwareSelectorBlock}>
                        {shouldShowWebcamHealthInformation && (
                            <div className={`webcamDeviceHealthCheckStatus ${styles.webcamDeviceHealthCheckStatus}`}>
                                <span className={styles.hardwareSelectorLabel}>
                                    {t('hardwareAccessCheck.camera')}
                                </span>
                                {shouldShowWebcamHealthStatus && (
                                    <WebcamDeviceHealthCheckStatus
                                        shouldShowWebcamHealthCheckInProgress={shouldShowWebcamHealthCheckInProgress}
                                    />)}
                            </div>)}

                        {showSelectWebcamDevice && (
                            <IconSelect icon="video-camera">
                                <select
                                    id="webcamDeviceSelector"
                                    className={styles.hardwareSelector}
                                    disabled={isReadOnly}
                                    onChange={this.handleSwitchWebcamDevice}
                                    value={webcamDeviceId}
                                    {...getA11yAttributesV2({
                                        'aria-label': this.getDeviceAriaLabel(
                                            'camera',
                                            shouldShowWebcamDiagnosisFailed,
                                            shouldShowWebcamHealthCheckInProgress
                                        ),
                                    })}
                                >
                                    {webcamOptions}
                                </select>
                            </IconSelect>
                        )}

                        {showWarningMissingWebcamDevice && (
                            <div className="webcamDeviceWarning">
                                {t('hardwareAccessCheck.missingWebcamDevice')}
                            </div>
                        )}

                        { showSelectAudioDevice && (
                            <MicDeviceHealthCheckStatus
                                shouldShowMicHealthInformation={shouldShowMicHealthInformation}
                                shouldShowMicHealthStatus={shouldShowMicHealthStatus}
                                shouldShowMicHealthCheckInProgress={shouldShowMicHealthCheckInProgress}
                            />
                        )}

                        {showSelectAudioDevice && (
                            <IconSelect icon="microphone">
                                <select
                                    id="audioDeviceSelector"
                                    className={styles.hardwareSelector}
                                    disabled={isReadOnly}
                                    onChange={this.handleSwitchAudioDevice}
                                    value={audioDeviceId}
                                    {...getA11yAttributesV2({
                                        'aria-label': this.getDeviceAriaLabel(
                                            'microphone',
                                            shouldShowMicDiagnosisFailed,
                                            shouldShowMicHealthCheckInProgress
                                        ),
                                    })}
                                >
                                    {audioOptions}
                                </select>
                            </IconSelect>
                        )}

                        {showWarningMissingAudioDevice && (
                            <div className="audioDeviceWarning">
                                {t('hardwareAccessCheck.missingAudioDevice')}
                            </div>
                        )}

                        {shouldShowSpeakerInformation && (
                            <span className={styles.hardwareSelectorLabel}>
                                {t('hardwareAccessCheck.speaker')}
                            </span>
                        )}
                        {showSelectSpeakerDevice && (
                            <IconSelect icon="volume-up">
                                <select
                                    id="speakerDeviceSelector"
                                    className={styles.hardwareSelector}
                                    disabled={isReadOnly}
                                    onChange={this.handleSwitchSpeakerDevice}
                                    value={
                                        this.speakerSelectValue(
                                            speakerDevices,
                                            speakerDeviceId
                                        )
                                    }
                                    {...getA11yAttributesV2({
                                        'aria-label': `${t('hardwareAccessCheck.speakerSelection')}, ${t('hardwareAccessCheck.selected')}`,
                                    })}
                                >
                                    {speakerOptions}
                                </select>
                            </IconSelect>
                        )}

                        <div>
                            <PlayTestSoundButtonWithAudio
                                speakerDeviceId={speakerDeviceId}
                                userMediaStream={shouldMuteMicWhenSoundTestIsOn ? webcamStream : null}
                                shouldMuteMicWhenSoundTestIsOn={shouldMuteMicWhenSoundTestIsOn}
                            />
                        </div>

                        {shouldShowWebcamDiagnosisFailed && (
                            <WebcamDiagnosisFailed onRetry={this.handleRetryWebcamHealthCheck} />
                        )}
                        {shouldShowMicDiagnosisFailed && (
                            <MicDiagnosisFailed onRetry={this.handleRetryMicHealthCheck} />
                        )}
                        <button
                            id="confirm-hw-settings-button"
                            className={`${styles.button} btn`}
                            onClick={this.handleConfirmSettings}
                            disabled={isConfirmHardwareSettingsButtonDisabled}
                            {...getA11yAttributesV2({
                                'aria-label': t('hardwareAccessCheck.continueButtonAriaLabel'),
                            })}
                        >
                            {t('hardwareAccessCheck.doConfirmHardwareSettings')}
                        </button>
                    </div>
                    <div className={styles.hardwarePreviewBlock}>
                        <LiveCamera stream={webcamStream} />
                        {shouldShowMicrophoneAudioLevelMeter && (
                            <MicrophoneAudioLevelMeter stream={webcamStream} />
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

function mapStateToProps(state) {
    return confirmHardwareSettingsSelector.mapStateToProps(state);
}

const ConfirmHardwareSettingsContainer = connect(mapStateToProps)(ConfirmHardwareSettings);
export default ConfirmHardwareSettingsContainer;
